<template>
  <div class="automated-email-settings-ui">
    <div class="automated-email-settings-ui__section">
      <VHeading :level="3" class="auto-email-header">Automation Mode</VHeading>
      <VHeading :level="4" class="auto-email-subheader">
        Choose how emails are handled. The system runs once daily at the scheduled hour.
      </VHeading>
    </div>

    <div class="automated-email-settings-ui__options">
      <div
        v-for="option in automationModeOptions"
        :key="option.id"
        :class="[
          'automated-email-settings-ui__option',
          mode === option.value && 'automated-email-settings-ui__option--selected',
          isAutomationModeOptionUnavailable(option) &&
            'automated-email-settings-ui__option--disabled',
        ]"
      >
        <VTooltip v-if="isModeDisabled && mode !== option.value" class="auto-email-tooltip">
          <template #activator="{ on, attrs }">
            <div v-bind="attrs" class="automated-email-settings-ui__option-trigger" v-on="on">
              <label class="automated-email-settings-ui__option-label" :for="option.id">
                <div class="automated-email-settings-ui__option-row">
                  <div
                    :class="[
                      'automated-email-settings-ui__option-icon',
                      AUTOMATION_MODE_ICON_CLASS[option.id],
                    ]"
                  >
                    <img
                      class="automated-email-settings-ui__option-icon-img"
                      :src="AUTOMATION_MODE_ICON[option.id]"
                      alt=""
                    />
                  </div>
                  <div class="automated-email-settings-ui__option-copy">
                    <div class="automated-email-settings-ui__option-title-row">
                      <span class="automated-email-settings-ui__option-title">{{
                        option.title
                      }}</span>
                      <span
                        :class="[
                          'automated-email-settings-ui__option-badge',
                          AUTOMATION_MODE_BADGE_CLASS[option.id],
                        ]"
                      >
                        {{ AUTOMATION_MODE_BADGE_LABEL[option.id] }}
                      </span>
                    </div>
                    <span class="automated-email-settings-ui__card-description">{{
                      option.description
                    }}</span>
                  </div>
                  <input
                    :id="option.id"
                    type="radio"
                    class="automated-email-settings-ui__option-radio"
                    name="automation-mode"
                    :value="option.value"
                    :checked="mode === option.value"
                    disabled
                  />
                </div>
              </label>
            </div>
          </template>
          You can only change this once per day. Available again tomorrow.
        </VTooltip>

        <div v-else-if="option.disabled" class="automated-email-settings-ui__option-trigger">
          <label class="automated-email-settings-ui__option-label" :for="option.id">
            <div class="automated-email-settings-ui__option-row">
              <div
                :class="[
                  'automated-email-settings-ui__option-icon',
                  AUTOMATION_MODE_ICON_CLASS[option.id],
                ]"
              >
                <img
                  class="automated-email-settings-ui__option-icon-img"
                  :src="AUTOMATION_MODE_ICON[option.id]"
                  alt=""
                />
              </div>
              <div class="automated-email-settings-ui__option-copy">
                <div class="automated-email-settings-ui__option-title-row">
                  <span class="automated-email-settings-ui__option-title">{{ option.title }}</span>
                  <span
                    :class="[
                      'automated-email-settings-ui__option-badge',
                      AUTOMATION_MODE_BADGE_CLASS[option.id],
                    ]"
                  >
                    {{ AUTOMATION_MODE_BADGE_LABEL[option.id] }}
                  </span>
                </div>
                <span class="automated-email-settings-ui__card-description">{{
                  option.description
                }}</span>
              </div>
              <input
                :id="option.id"
                type="radio"
                class="automated-email-settings-ui__option-radio"
                name="automation-mode"
                :value="option.value"
                :checked="mode === option.value"
                disabled
              />
            </div>
          </label>
        </div>

        <label v-else class="automated-email-settings-ui__option-label" :for="option.id">
          <div class="automated-email-settings-ui__option-row">
            <div
              :class="[
                'automated-email-settings-ui__option-icon',
                AUTOMATION_MODE_ICON_CLASS[option.id],
              ]"
            >
              <img
                class="automated-email-settings-ui__option-icon-img"
                :src="AUTOMATION_MODE_ICON[option.id]"
                alt=""
              />
            </div>
            <div class="automated-email-settings-ui__option-copy">
              <div class="automated-email-settings-ui__option-title-row">
                <span class="automated-email-settings-ui__option-title">{{ option.title }}</span>
                <span
                  :class="[
                    'automated-email-settings-ui__option-badge',
                    AUTOMATION_MODE_BADGE_CLASS[option.id],
                  ]"
                >
                  {{ AUTOMATION_MODE_BADGE_LABEL[option.id] }}
                </span>
              </div>
              <span class="automated-email-settings-ui__card-description">{{
                option.description
              }}</span>
            </div>
            <input
              :id="option.id"
              type="radio"
              class="automated-email-settings-ui__option-radio"
              name="automation-mode"
              :value="option.value"
              :checked="mode === option.value"
              @change="mode = option.value"
            />
          </div>

          <div
            v-if="
              option.value === EAutomationMode.SEMI_AUTOMATIC &&
              mode === EAutomationMode.SEMI_AUTOMATIC
            "
            class="automated-email-settings-ui__semi-auto-send"
            @click.stop
          >
            <div
              class="automated-email-settings-ui__semi-auto-send-radios"
              role="radiogroup"
              aria-label="Outbox automatic send timing"
            >
              <label
                v-for="choice in semiAutomaticAutoSendOptions"
                :key="choice.days"
                :class="[
                  'automated-email-settings-ui__semi-auto-send-row',
                  choice.disabled && 'automated-email-settings-ui__semi-auto-send-row--disabled',
                ]"
              >
                <input
                  v-model.number="semiAutomaticAutoSendDays"
                  type="radio"
                  class="automated-email-settings-ui__semi-auto-send-radio"
                  name="semi-automatic-auto-send"
                  :value="choice.days"
                  :disabled="choice.disabled"
                />
                <span class="automated-email-settings-ui__semi-auto-send-label">{{
                  choice.label
                }}</span>
              </label>
            </div>
          </div>
        </label>
      </div>
    </div>

    <div class="automated-email-settings-ui__section">
      <div class="automated-email-settings-ui__sender-label">
        <VHeading :level="3" class="auto-email-header">Send from</VHeading>
        <VIconTooltip class="ghost" :icon-src="infoIcon">
          Choose which patch user emails are sent from for automated and timed semi-automated sends.
        </VIconTooltip>
      </div>
      <span class="automated-email-settings-ui__card-description">
        Select the user whose name and email should appear as the sender.
      </span>
    </div>

    <div class="automated-email-settings-ui__sender">
      <div class="automated-email-settings-ui__sender-dropdown">
        <SenderSelect
          :model-value="senderUserId"
          :options="senderOptions"
          @update:model-value="setSenderUserId"
        />
      </div>
      <p
        v-if="isSelectedSenderDomainUnverified"
        class="automated-email-settings-ui__sender-warning"
      >
        This sender's email domain isn't verified — emails may fail to send.
      </p>
    </div>

    <div class="automated-email-settings-ui__section">
      <div class="automated-email-settings-ui__frequency-label">
        <VHeading :level="3" class="auto-email-header">Contact frequency</VHeading>
        <VIconTooltip class="ghost" :icon-src="infoIcon">
          Set the minimum number of days between emails sent to the same contact.
        </VIconTooltip>
      </div>
      <span class="automated-email-settings-ui__card-description">
        Minimum number of days between emails to the same contact ({{ CONTACT_FREQUENCY_MIN }}-{{
          CONTACT_FREQUENCY_MAX
        }}).
      </span>
    </div>

    <div class="automated-email-settings-ui__frequency-input">
      <VInput
        v-model="contactFrequencyInput"
        :config="{
          label: '',
          type: 'number',
          min: CONTACT_FREQUENCY_MIN,
          max: CONTACT_FREQUENCY_MAX,
        }"
      />
    </div>

    <div class="automated-email-settings-ui__save-wrap">
      <div class="automated-email-settings-ui__actions">
        <VBtn :loading="isSaving" :disabled="isLoading" @click="handleSave">Save</VBtn>
      </div>
    </div>

    <ModeSaveModal
      v-if="showSaveConfirmModal"
      :is-loading="isLoading"
      :is-saving="isSaving"
      @close="closeSaveConfirmModal"
      @confirm="confirmSaveAnyway"
    />

    <AutomatedEmailFilters
      :groups="filterGroups"
      :filters="filters"
      :is-loading="isLoadingFilters"
      :on-apply="savePatchFilters"
    />

    <AutomatedEmailTemplates
      :triggers="triggers"
      :is-lettings="isLettings"
      :is-saving="isSavingTemplates"
      :is-last-linked-template="isLastLinkedTemplate"
      :persist-template-edit="updateTemplate"
      @toggle-template="toggleTemplate"
      @save="saveTemplates"
    />
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { ComputedRef, Ref, WritableComputedRef } from 'vue';
  import type {
    TAutomationModeOption,
    TSemiAutomaticAutoSendOption,
  } from '~/modules/opportunities/automated-email/settings/types';
  import type { TAutomatedEmailPatchSender } from '~/modules/opportunities/automated-email/types';
  import { EAutomationMode } from '~/modules/opportunities/automated-email/types';

  // Config
  import {
    CONTACT_FREQUENCY_MIN,
    CONTACT_FREQUENCY_MAX,
    AUTOMATION_MODE_ICON,
    AUTOMATION_MODE_ICON_CLASS,
    AUTOMATION_MODE_BADGE_CLASS,
    AUTOMATION_MODE_BADGE_LABEL,
    getAutomationModeOptions,
    getSemiAutomaticAutoSendOptions,
  } from '~/modules/opportunities/automated-email/settings/config';

  // Composables
  import { useAutomationStatus } from '~/modules/opportunities/automated-email/settings/composables/use-automation-status';
  import { useTriggers } from '~/modules/opportunities/automated-email/settings/composables/use-triggers';
  import { usePatchFilters } from '~/modules/opportunities/automated-email/settings/composables/use-patch-filters';

  // Components
  import VHeading from '~/misc/v-heading/VHeading.vue';
  import VIconTooltip from '~/misc/v-icon-tooltip/VIconTooltip.vue';
  import VTooltip from '~/misc/v-tooltip/VTooltip.vue';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';
  import ModeSaveModal from '~/modules/opportunities/automated-email/settings/save-modal/ModeSaveModal.vue';
  import AutomatedEmailTemplates from '~/modules/opportunities/automated-email/settings/templates/AutomatedEmailTemplates.vue';
  import AutomatedEmailFilters from '~/modules/opportunities/automated-email/settings/filters/AutomatedEmailFilters.vue';
  import SenderSelect from '~/modules/opportunities/automated-email/settings/sender-select/SenderSelect.vue';

  // Assets
  import infoIcon from '~/assets/images/icons/info-icon.svg';

  const {
    patchId,
    saleType,
    isLettings,
    mode,
    semiAutomaticAutoSendDays,
    senderUserId,
    senderOptions,
    contactFrequency,
    isAutomationModeDirty,
    isModeDisabled,
    isLoading,
    isSaving,
    fetchStatus,
    saveStatus,
  } = useAutomationStatus();

  const automationModeOptions: ComputedRef<TAutomationModeOption[]> = computed<
    TAutomationModeOption[]
  >(() => getAutomationModeOptions(isLettings.value));

  const semiAutomaticAutoSendOptions: ComputedRef<TSemiAutomaticAutoSendOption[]> = computed<
    TSemiAutomaticAutoSendOption[]
  >(() => getSemiAutomaticAutoSendOptions(isLettings.value));

  const {
    triggers,
    isSavingTemplates,
    fetchTriggers,
    toggleTemplate,
    updateTemplate,
    saveTemplates,
    isLastLinkedTemplate,
  } = useTriggers();

  const {
    filters,
    filterGroups,
    isLoading: isLoadingFilters,
    fetchFilters,
    savePatchFilters,
  } = usePatchFilters();

  const showSaveConfirmModal: Ref<boolean> = ref<boolean>(false);

  const isAutomationModeOptionUnavailable = (option: TAutomationModeOption): boolean =>
    option.disabled === true || (isModeDisabled.value && mode.value !== option.value);

  const setShowSaveConfirmModal = (value: boolean): void => {
    showSaveConfirmModal.value = value;
  };

  const closeSaveConfirmModal = (): void => {
    setShowSaveConfirmModal(false);
  };

  const setSenderUserId = (value: number): void => {
    senderUserId.value = value;
  };

  const selectedSender: ComputedRef<TAutomatedEmailPatchSender | undefined> = computed<
    TAutomatedEmailPatchSender | undefined
  >(() =>
    senderOptions.value.find(
      (sender: TAutomatedEmailPatchSender) => sender.id === senderUserId.value
    )
  );

  const isSelectedSenderDomainUnverified: ComputedRef<boolean> = computed<boolean>(
    () => selectedSender.value !== undefined && !selectedSender.value.domain_verified
  );

  const contactFrequencyInput: WritableComputedRef<string> = computed<string>({
    get: () => String(contactFrequency.value),
    set: (value: string) => {
      const parsed = parseInt(value, 10);

      if (!isNaN(parsed)) {
        contactFrequency.value = Math.min(
          CONTACT_FREQUENCY_MAX,
          Math.max(CONTACT_FREQUENCY_MIN, parsed)
        );
      }
    },
  });

  const handleSave = async (): Promise<void> => {
    if (isAutomationModeDirty.value) {
      setShowSaveConfirmModal(true);

      return;
    }

    await saveStatus();
  };

  const confirmSaveAnyway = async (): Promise<void> => {
    await saveStatus();

    closeSaveConfirmModal();
  };

  watch(
    [patchId, saleType],
    ([nextPatchId, nextSaleType], [previousPatchId, previousSaleType]) => {
      if (
        nextPatchId === undefined ||
        (nextPatchId === previousPatchId && nextSaleType === previousSaleType)
      ) {
        return;
      }

      fetchStatus();
      fetchTriggers();
      fetchFilters();
    }
  );

  onMounted(() => {
    if (patchId.value !== undefined) {
      fetchStatus();
      fetchTriggers();
      fetchFilters();
    }
  });
</script>
